import React from "react"

import { Form, Formik, FormikHelpers } from "formik"
import { gql } from "urql"
import * as Yup from "yup"

import { useGradeOptions } from "hooks/remoteTable"
import { useMutation } from "hooks/urql"

import {
  AutocompleteField,
  Field,
  FormFailures,
  SelectField,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"

type AcrClassFormState = {
  id?: string
  schoolId: string
  teacherId: string
  name: string
  subjectId: string
  gradeId: string
}

type AcrClassTeacher = {
  id: string
  fullName: string
}

type AcrClassSchool = {
  id: string
  name: string
}

type AcrClass = {
  id: string
  name: string
  school: AcrClassSchool
  teacher: AcrClassTeacher
  subject: {
    id: string
    name: string
  }
  grade: {
    id: string
    name: string
  }
}

type AcrClassFormProps = {
  acrClass?: AcrClass
}

const ValidationSchema = Yup.object().shape({
  schoolId: Yup.string().required("School is required"),
  teacherId: Yup.string().required("Teacher is required"),
  name: Yup.string().required("Name is required"),
  subjectId: Yup.string().required("Subject is required"),
  gradeId: Yup.string().required("Grade is required"),
})

const AcrClassForm: React.FC<AcrClassFormProps> = ({ acrClass }) => {
  const [, saveAcrClass]: any[] = useMutation(acrClassMutation)
  const gradeOptions = useGradeOptions()

  const formState: AcrClassFormState = {
    id: acrClass?.id,
    schoolId: acrClass?.school.id || "",
    teacherId: acrClass?.teacher.id || "",
    name: acrClass?.name || "",
    subjectId: acrClass?.subject.id || "",
    gradeId: acrClass?.grade.id || "",
  }

  const handleSubmit = (
    values: AcrClassFormState,
    actions: FormikHelpers<AcrClassFormState>
  ) => {
    saveAcrClass({ input: values })
      .then((result: any) => {
        const { redirectTo, failures } = result.data.saveAcrClass
        if (failures.length > 0) {
          handleFailure(actions, failures)
        } else if (redirectTo) {
          window.location.href = redirectTo
        }
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <div className="w-full md:w-2/3 lg:w-1/2">
      <Formik
        initialValues={formState}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values }) => (
          <Form>
            <AutocompleteField
              name="schoolId"
              label="School"
              api="/api/admins/schools/autocomplete_name"
              defaultValue={
                acrClass?.school && {
                  value: acrClass.school.id,
                  label: acrClass.school.name,
                }
              }
            />
            <AutocompleteField
              name="teacherId"
              label="Teacher"
              api="/api/admins/teachers/autocomplete_full_name"
              searchOptions={`school_id=${values.schoolId}`}
              isDisabled={!values.schoolId}
              defaultValue={
                acrClass?.teacher && {
                  value: acrClass.teacher.id,
                  label: acrClass.teacher.fullName,
                }
              }
            />
            <Field name="name" label="Name" />
            <AutocompleteField
              name="subjectId"
              label="Subject"
              api="/api/admins/subjects/autocomplete_name"
              defaultValue={
                acrClass?.subject && {
                  value: acrClass.subject.id,
                  label: acrClass.subject.name,
                }
              }
            />
            <SelectField
              name="gradeId"
              label="Grade"
              options={gradeOptions}
              defaultValue={
                acrClass?.grade && {
                  value: acrClass.grade.id,
                  label: acrClass.grade.name,
                }
              }
            />
            <FormFailures />
            <SubmitButton />
          </Form>
        )}
      </Formik>
    </div>
  )
}

const acrClassMutation = gql`
  mutation ($input: AcrClassInputObject!) {
    saveAcrClass(input: $input) {
      redirectTo
      failures {
        message
      }
    }
  }
`

export default AcrClassForm
